import {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  createElement,
  type ComponentType,
  type DragEvent,
} from 'react';
import {
  FilePlus2,
  FileCode2,
  FileJson,
  FileText,
  FileCog,
  FileTerminal,
  FileLock2,
  File as FileIcon,
  FileUp,
  Upload,
  X,
  Pencil,
  Check,
  ChevronRight,
  CornerDownLeft,
} from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { Badge } from '@reflex/ui/components/ui/badge';
import { cn } from '@reflex/ui/lib/utils';

export interface FileMount {
  /** Absolute path inside the container, e.g. `/etc/app/config.json`. */
  key: string;
  /** Raw file content. */
  value: string;
}

export interface FileMountsEditorHandle {
  /** Returns the in-progress composer entry if it is valid, else null. */
  getPending: () => FileMount | null;
}

interface FileMountsEditorProps {
  entries: FileMount[];
  onChange: (entries: FileMount[]) => void;
  pathPlaceholder?: string;
  contentPlaceholder?: string;
}

/**
 * Cap on the size of a dropped/browsed file. File mounts are inlined as text
 * and baked into the image, so they should stay small — large or binary
 * payloads belong in the repository or a volume, not a mount. 256 KB
 * comfortably covers configs, scripts, certs, and `.env` files.
 */
const MAX_FILE_BYTES = 256 * 1024;

/**
 * Maps a dropped file's name to a container mount path. Mounts are keyed by an
 * absolute path (matching the `/path/to/file` placeholder), so a bare filename
 * is anchored at root; the user can edit it afterward via the card's editor.
 */
function toMountPath(fileName: string): string {
  const clean = fileName.trim().replace(/^\.\//, '');
  return clean.startsWith('/') ? clean : `/${clean}`;
}

type FileReadResult = { ok: true; mount: FileMount } | { ok: false; reason: string };

/**
 * Reads a single dropped/browsed file into a {@link FileMount}, rejecting files
 * that are too large or that look binary (a NUL byte is a reliable, cheap
 * heuristic since mount content is stored and transmitted as text).
 */
async function readFileAsMount(file: File): Promise<FileReadResult> {
  if (file.size > MAX_FILE_BYTES) {
    return { ok: false, reason: `${file.name} is too large (max ${formatBytes(MAX_FILE_BYTES)})` };
  }
  let text: string;
  try {
    text = await file.text();
  } catch {
    return { ok: false, reason: `Couldn't read ${file.name}` };
  }
  if (text.includes('\u0000')) {
    return { ok: false, reason: `${file.name} looks like a binary file` };
  }
  return { ok: true, mount: { key: toMountPath(file.name), value: text } };
}

// ---------------------------------------------------------------------------
// Derivation helpers
// ---------------------------------------------------------------------------

type LucideIcon = ComponentType<{ className?: string }>;

function iconForPath(path: string): LucideIcon {
  const name = (path.split('/').pop() ?? path).toLowerCase();
  const ext = name.includes('.') ? (name.split('.').pop() ?? '') : '';

  if (name.startsWith('.env') || name === 'env' || ext === 'env') return FileTerminal;
  if (name.endsWith('lock') || ext === 'lock' || name.endsWith('.pem') || name.endsWith('.key'))
    return FileLock2;

  switch (ext) {
    case 'json':
    case 'json5':
      return FileJson;
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
    case 'mjs':
    case 'cjs':
    case 'py':
    case 'go':
    case 'rs':
    case 'rb':
    case 'java':
    case 'c':
    case 'cc':
    case 'cpp':
    case 'h':
    case 'hpp':
    case 'php':
    case 'sql':
      return FileCode2;
    case 'sh':
    case 'bash':
    case 'zsh':
    case 'fish':
      return FileTerminal;
    case 'yml':
    case 'yaml':
    case 'toml':
    case 'ini':
    case 'conf':
    case 'cfg':
    case 'config':
      return FileCog;
    case 'md':
    case 'mdx':
    case 'txt':
    case 'log':
      return FileText;
    default:
      return FileIcon;
  }
}

/**
 * Renders the extension-derived file icon. Declared at module scope and using
 * `createElement` so the dynamically-selected icon is never treated as a
 * component created during render.
 */
function FileTypeIcon({ path, className }: { path: string; className?: string }) {
  return createElement(iconForPath(path), { className });
}

function splitPath(path: string): { dir: string; name: string } {
  const idx = path.lastIndexOf('/');
  if (idx < 0) return { dir: '', name: path };
  return { dir: path.slice(0, idx + 1), name: path.slice(idx + 1) };
}

function byteSize(content: string): number {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(content).length;
  return content.length;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function lineCount(content: string): number {
  if (content === '') return 0;
  return content.split('\n').length;
}

function FileMetaBadges({ content }: { content: string }) {
  const lines = lineCount(content);
  const bytes = byteSize(content);
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <Badge variant="subtle" className="font-mono text-2xs tabular-nums">
        {lines} {lines === 1 ? 'line' : 'lines'}
      </Badge>
      <Badge variant="subtle" className="font-mono text-2xs tabular-nums">
        {formatBytes(bytes)}
      </Badge>
    </div>
  );
}

function PathLabel({ path, className }: { path: string; className?: string }) {
  const { dir, name } = splitPath(path);
  return (
    <span className={cn('truncate font-mono text-sm', className)} title={path}>
      {dir && <span className="text-muted-foreground">{dir}</span>}
      <span className="text-foreground">{name}</span>
    </span>
  );
}

function CodePreview({ content }: { content: string }) {
  const lines = content.split('\n');
  return (
    <div className="max-h-64 overflow-auto rounded-md border border-border bg-background/60">
      <div className="min-w-full font-mono text-xs leading-relaxed">
        {lines.map((line, i) => (
          <div key={i} className="flex">
            <span className="sticky left-0 w-10 shrink-0 select-none border-r border-border/60 bg-muted/30 px-2 py-px text-right text-muted-foreground/60 tabular-nums">
              {i + 1}
            </span>
            <span className="whitespace-pre px-3 py-px text-foreground/90">{line || ' '}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Added file card
// ---------------------------------------------------------------------------

function FileMountCard({
  entry,
  duplicate,
  onSave,
  onRemove,
}: {
  entry: FileMount;
  duplicate: (path: string) => boolean;
  onSave: (next: FileMount) => boolean;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftPath, setDraftPath] = useState(entry.key);
  const [draftContent, setDraftContent] = useState(entry.value);

  const isDuplicate = editing && draftPath.trim() !== entry.key && duplicate(draftPath.trim());
  const canSave = draftPath.trim() !== '' && draftContent !== '' && !isDuplicate;

  function startEdit() {
    setDraftPath(entry.key);
    setDraftContent(entry.value);
    setEditing(true);
    setExpanded(false);
  }

  function commitEdit() {
    if (!canSave) return;
    const ok = onSave({ key: draftPath.trim(), value: draftContent });
    if (ok) setEditing(false);
  }

  if (editing) {
    return (
      <div className="overflow-hidden rounded-lg border border-primary/40 bg-card shadow-sm ring-1 ring-primary/20">
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
          <FileTypeIcon path={draftPath} className="icon-sm shrink-0 text-muted-foreground" />
          <Input
            value={draftPath}
            onChange={(e) => setDraftPath(e.target.value)}
            aria-label="Edit file path"
            className={cn(
              'h-7 flex-1 border-0 bg-transparent px-0 font-mono text-sm shadow-none focus-visible:ring-0',
              isDuplicate && 'text-destructive',
            )}
          />
        </div>
        <Textarea
          value={draftContent}
          onChange={(e) => setDraftContent(e.target.value)}
          aria-label="Edit file content"
          spellCheck={false}
          className="min-h-[120px] resize-y rounded-none border-0 bg-background/40 px-3 py-2 font-mono text-xs leading-relaxed shadow-none focus-visible:ring-0"
        />
        <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/40 px-3 py-2">
          <span className="text-2xs text-muted-foreground">
            {isDuplicate ? (
              <span className="text-destructive">A file with this path already exists.</span>
            ) : (
              <FileMetaBadges content={draftContent} />
            )}
          </span>
          <div className="flex items-center gap-1.5">
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={!canSave} onClick={commitEdit}>
              <Check className="icon-sm" />
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-border/80">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Collapse file preview' : 'Expand file preview'}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span className="grid size-7 shrink-0 place-items-center rounded-md border border-border/60 bg-muted/40">
            <FileTypeIcon path={entry.key} className="icon-sm text-muted-foreground" />
          </span>
          <PathLabel path={entry.key} />
          <ChevronRight
            className={cn(
              'icon-sm shrink-0 text-muted-foreground/60 transition-transform',
              expanded && 'rotate-90',
            )}
          />
        </button>

        <FileMetaBadges content={entry.value} />

        <div className="flex shrink-0 items-center">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={startEdit}
            aria-label="Edit file"
            className="text-muted-foreground hover:text-foreground"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onRemove}
            aria-label="Remove file"
            className="text-muted-foreground hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-muted/20 p-2">
          <CodePreview content={entry.value} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export const FileMountsEditor = forwardRef<FileMountsEditorHandle, FileMountsEditorProps>(
  function FileMountsEditor(
    {
      entries,
      onChange,
      pathPlaceholder = '/path/to/file',
      contentPlaceholder = 'Paste file content here...',
    },
    ref,
  ) {
    const [path, setPath] = useState('');
    const [content, setContent] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // The composer stays collapsed behind an "Add file" trigger until the user
    // opts in, so the editor reads as a compact list + button instead of a tall
    // always-open form taking up space before anyone needs it.
    const [composerOpen, setComposerOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dropError, setDropError] = useState<string | null>(null);
    // Tracks dragenter/dragleave nesting so moving the cursor across child
    // elements doesn't flicker the drop overlay; only a net-zero depth hides it.
    const dragDepth = useRef(0);

    const trimmedPath = path.trim();
    const pathExists = (candidate: string, ignore?: string) =>
      entries.some((e) => e.key === candidate && e.key !== ignore);
    const isDuplicate = trimmedPath !== '' && pathExists(trimmedPath);
    const canAdd = trimmedPath !== '' && content !== '' && !isDuplicate;

    useImperativeHandle(ref, () => ({
      getPending: () => (canAdd ? { key: trimmedPath, value: content } : null),
    }));

    // Read dropped/browsed files, dedupe by path (re-dropping a same-named file
    // replaces its content in place), and surface any rejections inline.
    async function ingestFiles(files: File[]) {
      if (files.length === 0) return;
      const accepted: FileMount[] = [];
      const errors: string[] = [];
      for (const file of files) {
        const result = await readFileAsMount(file);
        if (result.ok) accepted.push(result.mount);
        else errors.push(result.reason);
      }
      if (accepted.length > 0) {
        const merged = new Map(entries.map((e) => [e.key, e]));
        for (const mount of accepted) merged.set(mount.key, mount);
        onChange(Array.from(merged.values()));
      }
      setDropError(errors.length > 0 ? errors.join(' · ') : null);
    }

    const dragHasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes('Files');

    function handleDragEnter(e: DragEvent<HTMLDivElement>) {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setIsDragging(true);
    }

    function handleDragOver(e: DragEvent<HTMLDivElement>) {
      if (!dragHasFiles(e)) return;
      // Claim the drop so the browser doesn't navigate to the file or paste its
      // path into the content textarea.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }

    function handleDragLeave(e: DragEvent<HTMLDivElement>) {
      if (!dragHasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragging(false);
    }

    function handleDrop(e: DragEvent<HTMLDivElement>) {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      void ingestFiles(Array.from(e.dataTransfer.files));
    }

    function browse() {
      fileInputRef.current?.click();
    }

    function openComposer() {
      setDropError(null);
      setComposerOpen(true);
    }

    function cancelComposer() {
      setComposerOpen(false);
      setPath('');
      setContent('');
      setDropError(null);
    }

    function handleAdd() {
      if (!canAdd) return;
      onChange([...entries, { key: trimmedPath, value: content }]);
      setPath('');
      setContent('');
      // Collapse back to the compact trigger; re-open to add another.
      setComposerOpen(false);
    }

    function handleSaveEdit(index: number, next: FileMount): boolean {
      if (pathExists(next.key, entries[index]?.key)) return false;
      onChange(entries.map((e, i) => (i === index ? next : e)));
      return true;
    }

    function handleRemove(index: number) {
      onChange(entries.filter((_, i) => i !== index));
    }

    // Shared trigger pair reused by the empty state and the post-list footer so
    // the "open composer" / "browse" affordances stay identical in both spots.
    const addActions = (
      <>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={openComposer}
          aria-label="Add a file"
        >
          <FilePlus2 className="icon-sm" />
          Add file
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={browse} aria-label="Browse files">
          <Upload className="icon-sm" />
          Browse
        </Button>
      </>
    );

    return (
      <div
        className="relative space-y-3"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          data-testid="file-mounts-file-input"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            // Reset so picking the same file twice re-fires onChange.
            e.target.value = '';
            void ingestFiles(files);
          }}
        />

        {entries.length > 0 && (
          <div className="space-y-2">
            {entries.map((entry, i) => (
              <FileMountCard
                key={entry.key}
                entry={entry}
                duplicate={(candidate) => pathExists(candidate, entry.key)}
                onSave={(next) => handleSaveEdit(i, next)}
                onRemove={() => handleRemove(i)}
              />
            ))}
          </div>
        )}

        {entries.length === 0 && !composerOpen && (
          <div className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 bg-muted/10 px-4 py-6 text-center">
            <div className="grid size-9 place-items-center rounded-full bg-muted/50">
              <FilePlus2 className="icon-md text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No files mounted yet</p>
            <p className="max-w-xs text-xs text-muted-foreground">
              Drag &amp; drop files here, or add one. For small config files only, not large assets
              or binaries.
            </p>
            <div className="mt-1 flex items-center gap-1.5">{addActions}</div>
          </div>
        )}

        {dropError && (
          <p className="text-2xs text-destructive" role="alert">
            {dropError}
          </p>
        )}

        {/* Compact trigger once files exist — keeps the composer collapsed until needed. */}
        {entries.length > 0 && !composerOpen && (
          <div className="flex items-center gap-1.5">{addActions}</div>
        )}

        {/* Composer — a mini editor for the new file, revealed on demand. */}
        {composerOpen && (
          <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20">
            <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
              <FileTypeIcon
                path={trimmedPath || pathPlaceholder}
                className="icon-sm shrink-0 text-muted-foreground"
              />
              <Input
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder={pathPlaceholder}
                aria-label="New file path"
                spellCheck={false}
                autoFocus
                className={cn(
                  'h-7 flex-1 border-0 bg-transparent px-0 font-mono text-sm shadow-none placeholder:text-muted-foreground/60 focus-visible:ring-0',
                  isDuplicate && 'text-destructive',
                )}
              />
              {content !== '' && <FileMetaBadges content={content} />}
            </div>

            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder={contentPlaceholder}
              aria-label="New file content"
              spellCheck={false}
              className="min-h-[112px] resize-y rounded-none border-0 bg-background/40 px-3 py-2 font-mono text-xs leading-relaxed shadow-none placeholder:text-muted-foreground/50 focus-visible:ring-0"
            />

            <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/40 px-3 py-2">
              <span className="min-w-0 truncate text-2xs text-muted-foreground">
                {isDuplicate ? (
                  <span className="text-destructive">A file with this path already exists.</span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <CornerDownLeft className="h-3 w-3" />
                    <kbd className="font-sans">⌘</kbd>
                    <span className="-ml-0.5">+ Enter to add</span>
                  </span>
                )}
              </span>
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={cancelComposer}
                  aria-label="Cancel adding file"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={browse}
                  aria-label="Browse files"
                >
                  <Upload className="icon-sm" />
                  Browse
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={!canAdd}
                  onClick={handleAdd}
                  aria-label="Add file"
                >
                  <FilePlus2 className="icon-sm" />
                  Add file
                </Button>
              </div>
            </div>
          </div>
        )}

        {isDragging && (
          <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-primary/60 bg-background/80 backdrop-blur-sm">
            <FileUp className="icon-lg text-primary" />
            <p className="text-sm font-medium text-foreground">Drop files to mount</p>
            <p className="text-2xs text-muted-foreground">
              Up to {formatBytes(MAX_FILE_BYTES)} each
            </p>
          </div>
        )}
      </div>
    );
  },
);
